// Process-wide cache for the booking list view.
//
// Lives outside <Booking /> so that React-Router unmounts (clicking a
// row, opening the detail page, then clicking back) don't reset the
// rows to []. The list rehydrates from the cache on remount, then
// kicks a background refetch — that's the whole reason the back-nav
// no longer feels like a "hang."
//
// SECURITY: this module-level state survives a same-tab logout. The
// logout teardown path (Services/common.js → clearLocalStorageData)
// MUST call `invalidateBookingListCache()` so the next signed-in
// user can never see the previous user's bookings on first paint.

let bookingListCache = null;
let bookingListCacheKey = null;
let bookingListCacheAt = 0;

// 5 minutes — long enough to cover the typical "open detail, scroll
// around, come back" loop without showing genuinely stale data on a
// fresh session the next morning.
export const BOOKING_LIST_CACHE_TTL_MS = 5 * 60 * 1000;

// Filter arrays are sorted before stringifying so that toggling the
// same set of statuses in a different order still hits the same cache
// key — otherwise the back-nav would silently miss for filter sets
// that are semantically identical.
const sortedCopy = (arr) => [...(arr || [])].sort();

export function buildBookingListCacheKey({
  page,
  dates,
  query,
  status,
  drivers,
  packages,
}) {
  return JSON.stringify({
    page,
    startDate: dates?.startDate ? new Date(dates.startDate).toISOString() : null,
    endDate: dates?.endDate ? new Date(dates.endDate).toISOString() : null,
    query: query || "",
    status: sortedCopy(status),
    drivers: sortedCopy(drivers),
    packages: sortedCopy(packages),
  });
}

/**
 * Returns the cached list payload IFF the supplied key matches the
 * cached one AND the cached payload has at least one row AND we're
 * still inside the TTL window. Anything else returns null so the
 * caller falls back to a normal fetch + spinner.
 */
export function getFreshCachedBookingList(key) {
  if (!bookingListCache) return null;
  if (bookingListCacheKey !== key) return null;
  if ((bookingListCache?.data?.length || 0) === 0) return null;
  if (Date.now() - bookingListCacheAt >= BOOKING_LIST_CACHE_TTL_MS) return null;
  return bookingListCache;
}

export function setBookingListCache(payload, key) {
  bookingListCache = payload;
  bookingListCacheKey = key;
  bookingListCacheAt = Date.now();
}

/**
 * Drop the cached list. Called from the logout teardown so a different
 * user signing into the same tab cannot inherit the rows on first paint.
 */
export function invalidateBookingListCache() {
  bookingListCache = null;
  bookingListCacheKey = null;
  bookingListCacheAt = 0;
}
